import React from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { useTimeModal } from '../../../Components/TimeModal/TimeModalContext';
import { formatDate, formatDateTime, formatOverflow, formatNull, formatCaseStatus } from '../../../Utils/GlobalUtils';

import Loading from '../../../Components/Loading';
import LoadingLines from '../../../Components/LoadingLines';
import WarningPanel from '../../../Components/WarningPanel';
import ViewFileById from '../../ViewFileById';

import AddNewDocumentToApplication from '../../AddNewDocumentToApplication';

import styles from '../../../styles/DynamicModal.module.css';

import ButtonWithConfirmation from '../../ButtonWithConfirmation';

import IconContainer from '../../../Components/IconContainer';
import { ReactComponent as ReloadIcon } from '../../../icons/reload.svg';
import { ReactComponent as TrashIcon } from '../../../icons/trash.svg';
import { ReactComponent as FunnelIcon } from '../../../icons/funnel-bold.svg';
import { ReactComponent as FunnelFillIcon } from '../../../icons/funnel-fill.svg';
import FilterAMApplications from '../../FilterComponents/FilterAMApplications';
import DismissibleBanner from '../../DismissibleBanner';
import SearchAMActiveApplications from '../../SearchModals/SearchAMActiveApplications';

import headerPanel from '../../../styles/HeaderPanel.module.css';

export default function ActiveAMApplicationsCases({ userId, isFromAccount }) {
    const { openTimeModal } = useTimeModal();
    const location = useLocation();
    const navigate = useNavigate();

    const [cases, setCases] = React.useState([]);
    const [fioData, setFioData] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [reload, setReload] = React.useState(true);
    const [warning, setWarning] = React.useState('');

    const [limitForView, setLimitForView] = React.useState(20);
    const [lastUpdateTime, setLastUpdateTime] = React.useState('');

    const [access, setAccess] = React.useState(false);
    React.useEffect(() => {
        const token = localStorage.getItem('token');

        axios.get('/api/get-my-user-role', {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then(response => {
            const hasAccess = response.data.role === 'АппаратСРО';
            setAccess(hasAccess);
        }).catch(err => {
            console.error(`Ошибка получения данных: ${err}`);
        })
    }, []);


    React.useEffect(() => {
        if (!userId) return;

        const token = localStorage.getItem('token');
        setLoading(true);

        if (isFromAccount) {
            axios.get(`/api/get-important-account-data?userId=${userId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            }).then((response) => {
                setFioData(response.data);
            }).catch((error) => {
                console.error('Ошибка при получении данных пользователя:', error);
            })
        }

        axios.get(`/api/get-user-active-applications-cases`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            params: {
                user_id: userId,
                limitForView
            }
        }).then((response) => {
            setWarning('');
            setCases(response.data);
        })
        .catch((error) => {
            console.error(error);
            setWarning(error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее');
        })
        .finally(() => {
            setReload(false);
            setLoading(false);
            setLastUpdateTime(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
        });
    }, [reload, userId]);

    const [isSearch, setIsSearch] = React.useState(false);

    return (
    <>
        {isSearch && <SearchAMActiveApplications userId={userId} closeModal={() => setIsSearch(false)} />}

        <div className={headerPanel.panel}>
            <div className={headerPanel.searchBtn} onClick={() => { window.location.hash = 'search'; setIsSearch(true) }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M21.71,20.29,18,16.61A9,9,0,1,0,16.61,18l3.68,3.68a1,1,0,0,0,1.42,0A1,1,0,0,0,21.71,20.29ZM11,18a7,7,0,1,1,7-7A7,7,0,0,1,11,18Z" /></svg>
                Поиск...
            </div>
        </div>
        
        {!access &&
        <div className='horizontalContainer'>
            <div className='boxPage'>
                <div className='boxHeader'>
                    <h3>Информация</h3>
                </div>
                <div className='boxMain' style={{fontSize: '14px'}}>
                    <div>
                        Данная таблица отображает ваши активные заявки, (по которым СРО уже вас прикрепила). 
                        <br />
                        Чтобы посмотреть все предложения по заявкам, направленным вам СРО, перейдите в <Link className='aWithLink' to="/proposed-cases">"Витрину заявок"</Link>
                        <br />
                        Чтобы посмотреть заявки, на которые вы уже претендуете и по которым ожидается решение СРО перейдите во <Link className='aWithLink' to="/applications?section=pending-applications">вкладку "Заявки, по которым ожидается решение СРО"</Link>
                    </div>
                </div>
            </div>
        </div>}

        <div className='pageWrapper'>
            <div className='tableContainer'>
                <div className='tableHeaderWrapper'>
                    <div className='tableStatus'>
                        <div>
                            <h3>{isFromAccount && (loading ? 'Загрузка...' : `Заявки, к которым прикреплен управляющий (${fioData.lastname} ${fioData.firstname} ${fioData.surname})`)}</h3>
                        </div>
                        <a className='lastUpdateWrapper'>
                            Последнее обновление таблицы: {lastUpdateTime || '--:--'} 
                            {loading ? 
                                <Loading type='inline' /> 
                            : 
                                <IconContainer Icon={ReloadIcon} onClick={() => setReload(true)} size = '14' color = 'black' className = '' />
                            }
                        </a>
                    </div>
                    <div className='tableHeader' style={{gridTemplateColumns: '13fr 30fr 21fr 12fr 12fr 12fr'}}>
                        <div>Номер</div>
                        <div>Процедура / Должники</div>
                        <div>Арбитражный суд</div>
                        <div>Дата выбора управляющего СРО</div>
                        <div>Дата направления кандидатуры в суд</div>
                        <div>Дата судебного заседания о назначении</div>
                    </div>
                </div>
                {(cases.length===0 && loading) ? <LoadingLines /> 
                : warning ? <WarningPanel text={warning} inTable={true} buttonText='Обновить' buttonAction={() => setReload(true)} />
                : cases.map((caseItem) =>
                    <div 
                        className='tableRow' 
                        key={caseItem.id} 
                        style={{gridTemplateColumns: '13fr 30fr 21fr 12fr 12fr 12fr'}}
                        onClick={() => { 
                            navigate(`/case/${caseItem.id}`, { state: { from: location.pathname + location.search + location.hash }})
                        }}
                    >
                        <div>
                            {formatNull(caseItem.number)} 
                            {caseItem.nameCategory === 'Жалоба' && '(Жалоба)'} 
                            <span className='grayText'>{caseItem.internal_number}</span>
                        </div>
                        <div>
                            <p 
                                className='grayText'
                            >
                                {formatNull(caseItem.procedure_name)}
                            </p>
                            <span className='horizontalLine'></span>
                            {caseItem.debtors ?
                            <p>
                                {formatOverflow(caseItem.debtors.map((e) => e.name).join(', '))}
                            </p>
                            :
                            formatNull(caseItem.debtors)
                            }
                        </div>
                        <div>{formatNull(caseItem.court_name)}</div>
                        <div>
                            {formatDate(caseItem.date_candidate_selection)}
                        </div>
                        <div>
                            {formatDate(caseItem.date_candidate_selection)}
                        </div>
                        <div>
                            {formatDate(caseItem.date_court)}
                        </div>              
                    </div>
                )}
                {!warning && cases.length > 0 && (cases.length < cases[0].total_count) && (
                    <div className='tableLoadMoreRow' style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                        <div></div>
                        <div>
                            <button
                                type='button'
                                className='button'
                                style={{ width: 'fit-content' }}
                                onClick={() => {setLimitForView(prev => prev + 50); setReload(true)}}
                                disabled={loading}
                            >
                                {loading ? <Loading type='inline' /> : 'Загрузить ещё'}
                            </button>
                        </div>
                        <div></div>
                    </div>
                )}
            </div>
        </div>
    </>
    )
}
